/**
 * Profile tab — name + email. Email change requires current password
 * (anti-takeover). No profile photo by design (not a social product).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileTab = () => {
  const { user, checkAuth } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/account/profile`, { withCredentials: true })
      .then((r) => {
        setName(r.data.name || '');
        setEmail(r.data.email || '');
        setOriginalEmail(r.data.email || '');
      })
      .catch(() => toast.error('Could not load profile.'))
      .finally(() => setLoading(false));
  }, []);

  const emailChanged = email !== originalEmail;

  const save = async (e) => {
    e.preventDefault();
    if (emailChanged && !currentPassword) {
      toast.error('Current password is required to change your email.');
      return;
    }
    setSaving(true);
    try {
      const payload = { name };
      if (emailChanged) {
        payload.email = email;
        payload.current_password = currentPassword;
      }
      await axios.put(`${API}/account/profile`, payload, { withCredentials: true });
      toast.success('Profile updated.');
      setCurrentPassword('');
      setOriginalEmail(email);
      try { await checkAuth(); } catch (err) { console.warn('Profile auth refresh failed:', err); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-[#64748B]">Loading…</div>;

  return (
    <form onSubmit={save} className="bg-white border border-[#E2E8F0] rounded-xl p-6 max-w-xl" data-testid="profile-tab-form">
      <h2 className="text-xl font-bold text-[#020617] mb-1">Profile</h2>
      <p className="text-sm text-[#64748B] mb-6">Your name and login email. Used on receipts and email greetings.</p>

      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1.5"
            data-testid="profile-name-input"
            required
            maxLength={80}
          />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5"
            data-testid="profile-email-input"
            required
          />
          {emailChanged && (
            <p className="text-xs text-[#92400E] bg-[#FEF3C7] border border-[#FDE68A] rounded-md px-2 py-1.5 mt-2" data-testid="profile-email-changed-warning">
              Changing your login email — confirm with your current password below.
            </p>
          )}
        </div>
        {emailChanged && (
          <div>
            <Label htmlFor="current_password">Current password</Label>
            <Input
              id="current_password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="mt-1.5"
              data-testid="profile-current-password-input"
              autoComplete="current-password"
              required
            />
          </div>
        )}
        <div className="text-xs text-[#94A3B8]">
          Tier: <span className="font-medium text-[#475569]">{user?.tier || 'free'}</span> · Role: {user?.role || 'user'}
        </div>
      </div>

      <Button
        type="submit"
        disabled={saving}
        className="mt-6 bg-[#0052FF] text-white hover:bg-[#003ECC]"
        data-testid="profile-save-button"
      >
        {saving ? 'Saving…' : 'Save changes'}
      </Button>
    </form>
  );
};

export default ProfileTab;
